import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/common/StatusBadge";
import { EmptyState } from "@/components/common/EmptyState";
import { DataTable } from "@/components/common/DataTable";
import { toast } from "sonner";
import { ShieldCheck, CheckCircle2, XCircle, Clock } from "lucide-react";

const STATUS = [
  { key: "submitted", label: "Submitted" },
  { key: "in_review", label: "In Review" },
  { key: "big_initiated", label: "Screening Initiated" },
  { key: "big_in_progress", label: "In Progress" },
  { key: "cleared", label: "Cleared" },
  { key: "flagged", label: "Needs Attention" },
  { key: "completed", label: "Completed" },
];
const STATUS_LABEL = Object.fromEntries(STATUS.map((s) => [s.key, s.label]));
const STATUS_BADGE = { submitted: "in_review", in_review: "in_review", big_initiated: "in_review", big_in_progress: "in_review", cleared: "active", flagged: "inactive", completed: "active" };

const TABS = [{ key: "", label: "All" }, ...STATUS];

const BGC_COLUMNS = (onReview) => [
  { key: "request", header: "Request", width: 130, render: (r) => <span className="font-mono text-xs text-slate-500" data-testid={`bgc-row-${r.id}`}>{r.human_id}</span> },
  { key: "candidate", header: "Candidate", width: 220, render: (r) => (<><p className="font-medium text-slate-800 truncate">{r.person_name || `${r.legal_first_name} ${r.legal_last_name}`}</p><p className="text-xs text-slate-400 font-mono truncate">{r.person_human_id}</p></>) },
  { key: "district", header: "District", render: (r) => <span className="text-slate-600">{r.district_name || "—"}</span> },
  { key: "licenses", header: "Licenses", width: 110, render: (r) => <span className="text-slate-600">{(r.crm_licenses || []).length}</span> },
  { key: "big", header: "BIG Status", render: (r) => <span className="text-slate-600">{r.big_status || "—"}</span> },
  { key: "status", header: "Status", render: (r) => <StatusBadge status={STATUS_BADGE[r.status] || "in_review"} label={STATUS_LABEL[r.status] || r.status} /> },
  { key: "submitted", header: "Submitted", width: 130, render: (r) => <span className="text-slate-500">{(r.submitted_at || "").slice(0, 10)}</span> },
  { key: "__review", header: "", width: 104, render: (r) => <Button size="sm" variant="outline" onClick={() => onReview(r.id)} data-testid={`bgc-view-${r.id}`} className="h-8">Review</Button> },
];

export default function BackgroundChecks() {
  const [tab, setTab] = useState("");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/background-checks", { params: tab ? { status: tab } : {} });
      setItems(r.data.items || []); setCounts(r.data.counts || {});
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="flex items-center gap-2">
        <ShieldCheck className="w-5 h-5 text-[#006090]" />
        <h1 className="font-display text-2xl font-bold text-slate-900">Background &amp; Credit Checks</h1>
      </div>
      <p className="text-sm text-slate-500 mt-0.5">Review candidate requests and track the third-party (BIG) screening. Verify submitted insurance licenses in the CRM.</p>

      <div className="mt-6 flex flex-wrap gap-2 border-b border-slate-200">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`bgc-tab-${t.key || "all"}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.key ? "border-[#006090] text-[#006090]" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            {t.label}{t.key && counts[t.key] ? ` (${counts[t.key]})` : ""}
          </button>
        ))}
      </div>

      {loading ? <p className="text-sm text-slate-400 mt-8">Loading…</p> : items.length === 0 ? (
        <div className="mt-10"><EmptyState icon={ShieldCheck} title="No requests" description="No background check requests in this queue." /></div>
      ) : (
        <div className="mt-6"><DataTable columns={BGC_COLUMNS(setOpenId)} rows={items} resizeKey="bgc-list-columns" testId="bgc-table" /></div>
      )}

      {openId && <ReviewDialog id={openId} onClose={() => setOpenId(null)} onDone={load} />}
    </div>
  );
}

function ReviewDialog({ id, onClose, onDone }) {
  const [req, setReq] = useState(null);
  const [status, setStatus] = useState("");
  const [bigStatus, setBigStatus] = useState("");
  const [bigRef, setBigRef] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get(`/background-checks/${id}`).then((r) => {
    const d = r.data.request; setReq(d);
    setStatus(d.status || ""); setBigStatus(d.big_status || ""); setBigRef(d.big_reference || ""); setNotes(d.internal_notes || "");
  }).catch((e) => toast.error(apiError(e))), [id]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/background-checks/${id}`, { status, big_status: bigStatus, big_reference: bigRef, internal_notes: notes });
      toast.success("Updated"); onDone(); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const verify = async (lid, verification_status) => {
    try { await api.patch(`/background-checks/${id}/license/${lid}`, { verification_status }); toast.success("License updated"); load(); onDone(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!req) return null;
  const o = req.proposed_office || {}; const rr = req.retail_readiness || {};
  const rows = [
    ["Legal Name", [req.legal_first_name, req.legal_middle_name, req.legal_last_name, req.legal_suffix].filter(Boolean).join(" ")],
    ["Date of Birth", req.date_of_birth],
    ["Address", `${req.address || ""}, ${req.city || ""}, ${req.state || ""} ${req.postal_code || ""}`],
    ["Proposed Office", [o.name, o.address, o.city, o.state].filter(Boolean).join(", ")],
    ["Capital Available", rr.capital_available], ["Funding Source", rr.funding_source], ["Readiness Notes", rr.notes],
  ].filter(([, v]) => v && String(v).replace(/[, ]/g, ""));

  return (
    <Dialog open onOpenChange={(x) => !x && onClose()}>
      <DialogContent className="bg-white max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="bgc-review-dialog">
        <DialogHeader>
          <DialogTitle>{req.human_id} · {req.person_name}</DialogTitle>
          <DialogDescription>Submitted {(req.submitted_at || "").slice(0, 10)} · {req.district_name || "No district"}</DialogDescription>
        </DialogHeader>

        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
          {rows.map(([k, v]) => <div key={k} className="border-b border-slate-100 py-1.5"><p className="text-[11px] font-semibold text-slate-400 uppercase">{k}</p><p className="text-slate-700">{v}</p></div>)}
        </div>

        <div className="mt-4">
          <h3 className="font-semibold text-slate-800 text-sm">Insurance Licenses (CRM)</h3>
          {(req.crm_licenses || []).length === 0 ? <p className="text-sm text-slate-400 mt-1">None submitted.</p> : (
            <div className="mt-2 space-y-2">
              {req.crm_licenses.map((l) => (
                <div key={l.id} className="rounded-lg border border-slate-200 p-3 flex items-center justify-between gap-3" data-testid={`bgc-crm-license-${l.id}`}>
                  <div>
                    <p className="text-sm font-medium text-slate-800">{l.line} · {l.state}{l.license_number ? ` · #${l.license_number}` : ""}</p>
                    <p className="text-xs text-slate-400">{l.active ? "Active" : "Inactive"}{l.expiration ? ` · exp ${l.expiration}` : ""} · <span className="capitalize">{l.verification_status}</span></p>
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    <Button size="sm" variant="outline" onClick={() => verify(l.id, "verified")} data-testid={`bgc-verify-${l.id}`} className="h-8 text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5 mr-1" />Verify</Button>
                    <Button size="sm" variant="outline" onClick={() => verify(l.id, "rejected")} data-testid={`bgc-reject-${l.id}`} className="h-8 text-rose-600"><XCircle className="w-3.5 h-3.5 mr-1" />Reject</Button>
                    {l.verification_status !== "unverified" && <Button size="sm" variant="ghost" onClick={() => verify(l.id, "unverified")} className="h-8"><Clock className="w-3.5 h-3.5" /></Button>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
          <h3 className="font-semibold text-slate-800 text-sm">BIG Screening — internal only</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label className="text-xs font-semibold text-slate-600">Request Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="mt-1.5" data-testid="bgc-status-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{STATUS.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs font-semibold text-slate-600">BIG Status (free text)</Label><Input className="mt-1.5" data-testid="bgc-big-status" value={bigStatus} onChange={(e) => setBigStatus(e.target.value)} placeholder="e.g. Ordered, Pending Review" /></div>
          </div>
          <div><Label className="text-xs font-semibold text-slate-600">BIG Reference / Order #</Label><Input className="mt-1.5" data-testid="bgc-big-ref" value={bigRef} onChange={(e) => setBigRef(e.target.value)} /></div>
          <div><Label className="text-xs font-semibold text-slate-600">Internal Notes</Label><Textarea className="mt-1.5" data-testid="bgc-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} /></div>
          <p className="text-xs text-slate-400">Internal notes, BIG status and reference are never shown to the candidate.</p>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={save} disabled={busy} data-testid="bgc-save" className="bg-[#006090] hover:bg-[#004E75]">Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
